<template>
  <div v-if="user" class="min-h-screen bg-gray-100">
    <SidebarMenu :user="user" :isMenuOpen="isMenuOpen" @toggle-menu="toggleMenu" @close-menu="closeMenu"
      @logout="logout" />

    <div class="md:ml-64">
      <main class="p-4">
        <div class="p-4">
          <h1 class="text-xl font-bold mb-6">Facturación / Datos fiscales</h1>

          <div v-if="loading" class="flex justify-center my-8">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>

          <div class="mb-2 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded">
            Estos datos serán utilizados para generar las facturas de tus pagos de colegiatura. 
            Asegúrate de que toda la información sea correcta y esté actualizada.
          </div>

          <!-- Mensaje informativo adicional del backend -->
          <div v-if="billingInfo" class="mb-6 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded whitespace-pre-line">
            {{ billingInfo }}
          </div>

          <div>
            <form @submit.prevent="saveBillingData" class="mt-6">
              <!-- Información fiscal -->
              <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white p-6 mb-6">
                <h2 class="text-lg font-semibold mb-4">Información Fiscal</h2>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label for="rfc" class="block text-sm font-medium text-gray-500">RFC*</label>
                      <input id="rfc" v-model="formData.rfc" type="text" required
                        minlength="12" maxlength="13" @input="validateRFC"
                        :class="[
                          'mt-1 block w-full rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500',
                          rfcError ? 'border-red-300' : 'border-gray-300'
                      ]" />
                    <p v-if="rfcError" class="mt-1 text-sm text-red-600">{{ rfcError }}</p>
                  </div>

                  <div>
                    <label for="idregimen" class="block text-sm font-medium text-gray-500">Régimen Fiscal*</label>
                    <select id="idregimen" v-model="formData.idregimen" required
                      class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
                      <option value="">Seleccionar Régimen</option>
                      <option value="601">601 - General de Ley Personas Morales</option>
                      <option value="603">603 - Personas Morales con Fines no Lucrativos</option>
                      <option value="605">605 - Sueldos y Salarios e Ingresos Asimilados a Salarios</option>
                      <option value="606">606 - Arrendamiento</option>
                      <option value="607">607 - Régimen de Enajenación o Adquisición de Bienes</option>
                      <option value="608">608 - Demás ingresos</option>
                      <option value="610">610 - Residentes en el Extranjero sin Establecimiento Permanente en México</option>
                      <option value="611">611 - Ingresos por Dividendos (socios y accionistas)</option>
                      <option value="612">612 - Personas Físicas con Actividades Empresariales y Profesionales</option>
                      <option value="614">614 - Ingresos por intereses</option>
                      <option value="615">615 - Régimen de los ingresos por obtención de premios</option>
                      <option value="616">616 - Sin obligaciones fiscales</option>
                      <option value="620">620 - Sociedades Cooperativas de Producción que optan por diferir sus ingresos</option>
                      <option value="621">621 - Incorporación Fiscal</option>
                      <option value="622">622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras</option>
                      <option value="623">623 - Opcional para Grupos de Sociedades</option>
                      <option value="624">624 - Coordinados</option>
                      <option value="625">625 - Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas</option>
                      <option value="626">626 - Régimen Simplificado de Confianza</option>
                    </select>
                  </div>

                  <div>
                    <label for="idcfdi" class="block text-sm font-medium text-gray-500">Uso de CFDI*</label>
                    <select id="idcfdi" v-model="formData.idcfdi" required
                      class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
                      <option value="D10">D10 - Pagos por servicios educativos (colegiaturas)</option>
                      <option value="G03">G03 - Gastos en general</option>
                    </select>
                    <p class="mt-1 text-xs text-gray-500">Para colegiaturas, el uso recomendado es D10</p>
                  </div>
                </div>
              </div>

              <!-- Información de contacto -->
              <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white p-6 mb-6">
                <h2 class="text-lg font-semibold mb-4">Información de Contacto</h2>
                <div class="grid grid-cols-1 gap-6">
                  <div>
                    <label for="email" class="block text-sm font-medium text-gray-500">Correo Electrónico para Facturas*</label>
                    <div class="flex flex-col md:flex-row gap-2">
                      <input id="email" v-model="formData.email" type="email" required
                        :disabled="formData.emailVerified && !allowEmailChange"
                        @input="handleEmailChange"
                        @blur="checkEmailVerification"
                        :class="[
                          'mt-1 flex-1 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500',
                          emailError ? 'border-red-300' : 'border-gray-300',
                          formData.emailVerified && !allowEmailChange ? 'bg-green-50 border-green-300' : ''
                        ]" />
                      
                      <!-- Botón Verificar -->
                      <button type="button" 
                        v-if="!formData.emailVerified || allowEmailChange"
                        @click="sendVerificationCode"
                        :disabled="!isValidEmail(formData.email) || sendingCode || checkingEmail"
                        class="mt-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed">
                        <span v-if="checkingEmail">Verificando...</span>
                        <span v-else-if="sendingCode">Enviando...</span>
                        <span v-else>Verificar</span>
                      </button>
                      
                      <!-- Estado Verificado -->
                      <div v-else-if="formData.emailVerified && !allowEmailChange" 
                          class="mt-1 px-4 py-2 bg-green-100 text-green-700 rounded-md flex items-center">
                        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        Verificado
                      </div>
                      
                      <!-- Botón Cambiar Email -->
                      <button type="button" 
                        v-if="formData.emailVerified && !allowEmailChange"
                        @click="enableEmailChange"
                        class="mt-1 px-4 py-2 rounded-md text-white bg-blue-600 hover:bg-blue-700">
                        Cambiar
                      </button>
                    </div>
                    
                    <p v-if="emailError" class="mt-1 text-sm text-red-600">{{ emailError }}</p>
                    <p v-if="!formData.emailVerified || allowEmailChange" class="mt-1 text-xs text-gray-500 hidden">
                      No usar correos institucionales
                    </p>
                    <p v-else-if="formData.emailVerified && emailVerificationDate" class="mt-1 text-xs text-green-600 hidden">
                      Verificado el {{ formatDate(emailVerificationDate) }}
                    </p>
                  </div>

                  <!-- Campo de código de verificación -->
                  <div v-if="showVerificationCode">
                    <label for="verificationCode" class="block text-sm font-medium text-gray-500">Código de Verificación*</label>
                    <div class="flex gap-2">
                      <input id="verificationCode" v-model="verificationCode" type="text" 
                        maxlength="6" placeholder="Ingresa el código recibido"
                        @keyup.enter="validateCode"
                        class="mt-1 flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" />
                      <button type="button" 
                        @click="validateCode"
                        :disabled="!verificationCode || validatingCode"
                        class="mt-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed">
                        {{ validatingCode ? 'Validando...' : 'Validar' }}
                      </button>
                      <button type="button" 
                        @click="cancelVerification"
                        class="mt-1 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700">
                        Cancelar
                      </button>
                    </div>
                    <p class="mt-1 text-xs text-gray-500">
                      El código expira en 10 minutos. Si no lo recibiste, revisa tu carpeta de spam.
                    </p>
                  </div>
                </div>
              </div>

              <!-- Botones de acción -->
              <div class="flex flex-col sm:flex-row gap-3 justify-end mt-6">
                <button type="button"
                  class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  @click="resetForm">
                  <svg class="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" stroke-width="2"
                    viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Cancelar
                </button>
                
                <button type="submit"
                  :disabled="!canSaveForm"
                  :class="[
                    'inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
                    canSaveForm 
                      ? 'text-white bg-blue-600 hover:bg-blue-700' 
                      : 'text-gray-400 bg-gray-300 cursor-not-allowed'
                  ]">
                  <svg v-if="!loading" class="w-4 h-4 mr-2" fill="none" stroke="currentColor"
                    stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7" />
                  </svg>
                  {{ loading ? 'Guardando...' : 'Guardar Datos' }}
                </button>
              </div>
              
              <!-- Mensaje de ayuda para botón deshabilitado -->
              <div v-if="!canSaveForm" class="mt-2 text-center">
                <p class="text-sm text-red-600">
                  <svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L5.082 15.5c-.77.833.192 2.5 1.732 2.5z"></path>
                  </svg>
                  Debes verificar tu correo electrónico antes de poder guardar los datos
                </p>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SidebarMenu from '@/components/SidebarMenu.vue';
import { 
  getBillingData, 
  createBillingData,
  updateBillingData, 
  verifyEmail, 
  validateVerificationCode,
  checkEmailVerificationStatus,
  clearAuthToken 
} from '@/services/api';

export default {
  components: {
    SidebarMenu
  },
  data() {
    return {
      user: null,
      isMenuOpen: false,
      loading: false,
      sendingCode: false,
      validatingCode: false,
      checkingEmail: false,
      showVerificationCode: false,
      verificationCode: '',
      rfcError: '',
      emailError: '',
      hasExistingData: false,
      allowEmailChange: false,
      emailVerificationDate: null,
      billingInfo: '', 
      formData: {
        idstudent: '',
        rfc: '',
        idregimen: '',
        idcfdi: 'D10',
        email: '',
        emailVerified: false
      },
      originalData: {}
    };
  },
  computed: {
    canSaveForm() {
      return (
        !this.loading && 
        !this.rfcError && 
        this.formData.emailVerified && 
        this.formData.rfc && 
        this.formData.idregimen && 
        this.formData.email &&
        !this.allowEmailChange
      );
    }
  },
  async created() {
    await this.getUserFromBackend();
    if (this.user) {
      await this.loadBillingData();
    }
  },
  methods: {
    validateRFC() {
      const rfc = this.formData.rfc.toUpperCase();
      this.formData.rfc = rfc;

      const rfcPersonaFisica = /^[A-ZÑ&]{4}[0-9]{6}[A-Z0-9]{3}$/;
      const rfcPersonaMoral = /^[A-ZÑ&]{3}[0-9]{6}[A-Z0-9]{3}$/;

      if (rfc.length >= 12 && rfc.length <= 13) {
        if (!rfcPersonaFisica.test(rfc) && !rfcPersonaMoral.test(rfc)) {
          this.rfcError = 'Formato de RFC inválido';
        } else {
          this.rfcError = '';
        }
      } else if (rfc.length > 0) {
        this.rfcError = 'El RFC debe tener entre 12 y 13 caracteres';
      } else {
        this.rfcError = '';
      }
    },

    isValidEmail(email) {
      if (!email) return false;
      
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const allowedExtensions = ['.com', '.edu.mx', '.mx', '.net', '.org'];
      
      return emailRegex.test(email) && 
             allowedExtensions.some(ext => email.endsWith(ext));
    },

    handleEmailChange() {
      // Si el email cambió, resetear verificación
      if (this.formData.email !== this.originalData.email) {
        this.formData.emailVerified = false;
        this.allowEmailChange = true;
        this.showVerificationCode = false;
        this.verificationCode = '';
        this.emailError = '';
        this.emailVerificationDate = null;
      }
    },

    async checkEmailVerification() {
      if (!this.isValidEmail(this.formData.email) || this.allowEmailChange) {
        return;
      }

      try {
        this.checkingEmail = true;
        this.emailError = '';

        const response = await checkEmailVerificationStatus(this.formData.email);
        
        if (response.success && response.data.verified) {
          this.formData.emailVerified = true;
          this.allowEmailChange = false;
          this.emailVerificationDate = response.data.verified_at;
          this.showVerificationCode = false;
          this.verificationCode = '';
        } else {
          this.formData.emailVerified = false;
        }

      } catch (error) {
        console.error('Error al verificar estado del email:', error);
        this.formData.emailVerified = false;
      } finally {
        this.checkingEmail = false;
      }
    },

    enableEmailChange() {
      this.allowEmailChange = true;
      this.formData.emailVerified = false;
      this.emailVerificationDate = null;
      this.showVerificationCode = false;
      this.verificationCode = '';
      this.$nextTick(() => {
        document.getElementById('email').focus();
      });
    },

    async sendVerificationCode() {
      if (!this.isValidEmail(this.formData.email)) {
        this.emailError = 'Correo inválido o es institucional';
        return;
      }

      try {
        this.sendingCode = true;
        this.emailError = '';

        // Pasar el email del estudiante para determinar la institución
        const studentEmail = this.user?.email;
        const response = await verifyEmail(this.formData.email, studentEmail);
        
        if (response.success) {
          this.showVerificationCode = true;
          await this.$alert(
            'Código enviado',
            `Se ha enviado un código de verificación a ${this.formData.email}. El código expira en ${response.data.expires_in_minutes} minutos.`,
            'success'
          );
        }

      } catch (error) {
        console.error('Error al enviar código:', error);
        
        let errorMessage = 'Error al enviar el código de verificación';
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        
        this.emailError = errorMessage;
      } finally {
        this.sendingCode = false;
      }
    },

    async validateCode() {
      if (!this.verificationCode || this.verificationCode.length !== 6) {
        await this.$alert(
          'Código incompleto',
          'Por favor, ingresa el código de 6 dígitos completo.',
          'warning'
        );
        return;
      }

      try {
        this.validatingCode = true;

        const response = await validateVerificationCode(this.formData.email, this.verificationCode);
        
        if (response.success) {
          this.formData.emailVerified = true;
          this.allowEmailChange = false;
          this.showVerificationCode = false;
          this.verificationCode = '';
          this.emailVerificationDate = new Date().toISOString();

          await this.$alert(
            'Email verificado',
            'Tu correo electrónico ha sido verificado correctamente',
            'success'
          );
        }

      } catch (error) {
        console.error('Error al validar código:', error);
        
        let errorMessage = 'El código ingresado no es válido. Intenta nuevamente.';
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        
        await this.$alert(
          'Código inválido',
          errorMessage,
          'error'
        );
      } finally {
        this.validatingCode = false;
      }
    },

    cancelVerification() {
      this.showVerificationCode = false;
      this.verificationCode = '';
      
      // Si estaba cambiando el email, revertir al original
      if (this.allowEmailChange && this.originalData.emailVerified) {
        this.formData.email = this.originalData.email;
        this.formData.emailVerified = true;
        this.allowEmailChange = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    async getUserFromBackend() {
      try {
        const response = await axios.get('/me');
        this.user = response.data.user;
      } catch (error) {
        console.error("Error obteniendo usuario", error);
        this.$router.push('/login');
      }
    },

    async loadBillingData() {
      try {
        this.loading = true;
        const studentId = this.user.email.match(/^a(\d+)@/)[1];
        
        const response = await getBillingData(studentId);
        
        // Ahora getBillingData siempre retorna datos (al menos idstudent e info)
        const billingDataArray = response.data;
        
        if (Array.isArray(billingDataArray) && billingDataArray.length > 0) {
          const billingData = billingDataArray[0];
          
          // Extraer el mensaje informativo
          this.billingInfo = billingData.info || '';
          
          // Verificar si hay datos completos o solo la estructura base
          const hasCompleteData = billingData.rfc && billingData.rfc.trim() !== '';
          
          this.formData = {
            idstudent: studentId,
            rfc: billingData.rfc || '',
            idregimen: billingData.idregimen || '',
            idcfdi: billingData.idcfdi || 'D10',
            email: billingData.email || '',
            emailVerified: false // Verificar estado del email
          };
          
          this.hasExistingData = hasCompleteData;
          
          // Verificar estado del email si existe
          if (this.formData.email && this.formData.email.trim() !== '') {
            await this.checkEmailVerification();
          }
        } else {
          this.formData.idstudent = studentId;
          this.hasExistingData = false;
          this.billingInfo = '';
        }
        
        this.originalData = { ...this.formData };

      } catch (error) {
        console.error('Error al cargar datos de facturación:', error);
        
        // Preparar formulario para nuevos datos en caso de error
        const studentId = this.user.email.match(/^a(\d+)@/)[1];
        this.formData.idstudent = studentId;
        this.hasExistingData = false;
        this.billingInfo = '';
        
        await this.$alert(
          'Error',
          'No se pudieron cargar los datos de facturación.',
          'error'
        );
      } finally {
        this.loading = false;
      }
    },

    async saveBillingData() {
      if (this.rfcError) {
        await this.$alert(
          'RFC inválido',
          'Por favor, corrige el RFC antes de continuar.',
          'error'
        );
        return;
      }

      if (!this.formData.emailVerified) {
        await this.$alert(
          'Email no verificado',
          'Debes verificar tu correo electrónico antes de guardar los datos.',
          'warning'
        );
        return;
      }

      try {
        this.loading = true;
        const studentId = this.user.email.match(/^a(\d+)@/)[1];

        const payload = {
          idstudent: studentId,
          rfc: this.formData.rfc,
          idcfdi: this.formData.idcfdi,
          idregimen: this.formData.idregimen,
          email: this.formData.email
        };

        let response;
        console.log('Guardando datos. hasExistingData:', this.hasExistingData);
        
        if (this.hasExistingData) {
          // Actualizar datos existentes
          console.log('Actualizando datos existentes...');
          response = await updateBillingData(studentId, payload);
        } else {
          // Crear nuevos datos
          console.log('Creando nuevos datos...');
          response = await createBillingData(payload);
          this.hasExistingData = true; // Marcar que ahora ya existen datos
        }
        console.log(response);
        this.loading = false;
        // Actualizar datos originales
        this.originalData = { ...this.formData };
        this.allowEmailChange = false;

        await this.$alert(
          'Datos guardados',
          'Tus datos de facturación han sido actualizados correctamente.',
          'success'
        );

      } catch (error) {
        console.error('Error al guardar datos:', error);
        
        // Manejo de errores específicos del backend
        let errorMessage = 'No se pudieron guardar los datos. Intenta nuevamente.';
        
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.status === 422) {
          errorMessage = 'Los datos proporcionados no son válidos.';
        } else if (error.response?.status === 400) {
          errorMessage = 'Solicitud incorrecta. Verifica los datos.';
        }

        await this.$alert(
          'Error al guardar',
          errorMessage,
          'error'
        );
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.formData = { ...this.originalData };
      this.rfcError = '';
      this.emailError = '';
      this.showVerificationCode = false;
      this.verificationCode = '';
      this.allowEmailChange = false;
      
      // Si tenía email verificado originalmente, restaurar estado
      if (this.originalData.emailVerified) {
        this.formData.emailVerified = true;
      }
    },

    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },

    closeMenu() {
      this.isMenuOpen = false;
    },

    async logout() {
      try {
        clearAuthToken();
        await axios.get('/sanctum/csrf-cookie');
        await new Promise(resolve => setTimeout(resolve, 100));
        const response = await axios.post('/logout', {}, {
          withCredentials: true,
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        console.log(response);
        document.cookie.split(";").forEach(function (c) {
          document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });
        this.$router.push('/login');
      } catch (error) {
        console.error('Error al cerrar sesión:', error.response?.data || error);
      }
    }
  }
};
</script>